<template>
    <div v-if="show" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h3>Изменить статус кампании</h3>
                <button class="modal-close" @click="closeModal">&times;</button>
            </div>

            <div class="modal-body">
                <div class="form-group">
                    <label for="status-select">Новый статус:</label>
                    <select id="status-select" v-model="selectedStatus" class="form-control" :disabled="loading">
                        <option value="">Выберите статус</option>
                        <option v-for="status in availableStatuses" :key="status.value" :value="status.value">
                            {{ status.label }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="comment-input">Комментарий:</label>
                    <textarea id="comment-input" v-model="comment" class="form-control" rows="3"
                        placeholder="Введите комментарий к изменению статуса (необязательно)..." :disabled="loading"
                        maxlength="1000"></textarea>
                    <div class="char-count">{{ comment.length }}/1000</div>
                </div>
            </div>

            <div class="modal-footer">
                <button class="btn btn--secondary" @click="closeModal" :disabled="loading">
                    Отмена
                </button>
                <button class="btn btn--primary" @click="confirmChange" :disabled="!canConfirm || loading">
                    <span v-if="loading">Изменение...</span>
                    <span v-else>Изменить статус</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StatusChangeModal',
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        campaign: {
            type: Object,
            default: null,
        },
        currentStatus: {
            type: String,
            default: '',
        },
        isAdmin: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedStatus: '',
            comment: '',
            loading: false,
        };
    },
    computed: {
        availableStatuses() {
            const statusMap = {
                approve: { value: 'approve', label: 'approve' },
                APPROVE: { value: 'APPROVE', label: 'APPROVE' },
                review: { value: 'review', label: 'review' },
                REVIEW: { value: 'REVIEW', label: 'REVIEW' },
                reserve: { value: 'reserve', label: 'reserve' },
                RESERVE: { value: 'RESERVE', label: 'RESERVE' },
                booked: { value: 'booked', label: 'booked' },
                BOOKED: { value: 'BOOKED', label: 'BOOKED' },
                active: { value: 'active', label: 'active' },
                ACTIVE: { value: 'ACTIVE', label: 'ACTIVE' },
                activated: { value: 'activated', label: 'activated' },
                ACTIVATED: { value: 'ACTIVATED', label: 'ACTIVATED' },
                completed: { value: 'completed', label: 'completed' },
                COMPLETED: { value: 'COMPLETED', label: 'COMPLETED' },
                rejected: { value: 'rejected', label: 'rejected' },
                REJECTED: { value: 'REJECTED', label: 'REJECTED' },
                cancel: { value: 'cancel', label: 'cancel' },
                CANCEL: { value: 'CANCEL', label: 'CANCEL' },
                cancelled: { value: 'cancelled', label: 'cancelled' },
                CANCELLED: { value: 'CANCELLED', label: 'CANCELLED' },
                processing: { value: 'processing', label: 'processing' },
                PROCESSING: { value: 'PROCESSING', label: 'PROCESSING' },
                paused: { value: 'paused', label: 'paused' },
                PAUSED: { value: 'PAUSED', label: 'PAUSED' },
            };

            // Для не-админов: только переход активной кампании в статус "paused"
            if (!this.isAdmin) {
                const currentStatusLower = this.normalizeStatus(this.currentStatus);
                const isActive = currentStatusLower === 'active' || currentStatusLower === 'activated';
                
                if (isActive) {
                    return [statusMap.paused, statusMap.PAUSED];
                }
                
                // Если кампания не активна, не-админы не могут менять статус
                return [];
            }

            // Для админов: разрешаем переход из любого статуса в любой (API сам проверит валидность)
            return Object.values(statusMap);

            /* Старая логика с ограничениями переходов (закомментировано)
            // Определяем доступные статусы в зависимости от текущего статуса
            // Используем реальные ограничения API на основе полученной ошибки
            const current = this.normalizeStatus(this.currentStatus);
            const available = [];

            switch (current) {
                case 'approve':
                case 'APPROVE':
                    available.push(
                        statusMap.review,
                        statusMap.REVIEW,
                        statusMap.rejected,
                        statusMap.REJECTED,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'review':
                case 'REVIEW':
                    // Из review разрешаем все возможные переходы (API сам проверит валидность)
                    available.push(
                        statusMap.approve,
                        statusMap.APPROVE,
                        statusMap.review,
                        statusMap.REVIEW,
                        statusMap.reserve,
                        statusMap.RESERVE,
                        statusMap.rejected,
                        statusMap.REJECTED,
                        statusMap.booked,
                        statusMap.BOOKED,
                        statusMap.cancel,
                        statusMap.CANCEL,
                        statusMap.cancelled,
                        statusMap.CANCELLED,
                        statusMap.active,
                        statusMap.ACTIVE,
                        statusMap.activated,
                        statusMap.ACTIVATED,
                        statusMap.completed,
                        statusMap.COMPLETED,
                        statusMap.processing,
                        statusMap.PROCESSING
                    );
                    break;
                case 'reserve':
                case 'RESERVE':
                    available.push(
                        statusMap.booked,
                        statusMap.BOOKED,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'booked':
                case 'BOOKED':
                    available.push(
                        statusMap.active,
                        statusMap.ACTIVE,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'active':
                case 'ACTIVE':
                    available.push(
                        statusMap.activated,
                        statusMap.ACTIVATED,
                        statusMap.completed,
                        statusMap.COMPLETED,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'activated':
                case 'ACTIVATED':
                    available.push(
                        statusMap.completed,
                        statusMap.COMPLETED,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'completed':
                case 'COMPLETED':
                    // Завершенные кампании нельзя изменить
                    break;
                case 'rejected':
                case 'REJECTED':
                    available.push(
                        statusMap.approve,
                        statusMap.APPROVE,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                case 'cancelled':
                case 'CANCELLED':
                    // Отмененные кампании нельзя изменить
                    break;
                case 'processing':
                case 'PROCESSING':
                    // Из processing можно перейти в booked или cancelled
                    available.push(
                        statusMap.booked,
                        statusMap.BOOKED,
                        statusMap.cancelled,
                        statusMap.CANCELLED
                    );
                    break;
                default:
                    // Для неизвестных статусов показываем основные переходы
                    available.push(
                        statusMap.approve,
                        statusMap.APPROVE,
                        statusMap.review,
                        statusMap.REVIEW,
                        statusMap.reserve,
                        statusMap.RESERVE,
                        statusMap.booked,
                        statusMap.BOOKED,
                        statusMap.active,
                        statusMap.ACTIVE,
                        statusMap.rejected,
                        statusMap.REJECTED,
                        statusMap.cancelled,
                        statusMap.CANCELLED,
                        statusMap.processing,
                        statusMap.PROCESSING
                    );
            }

            return available;
            */
        },
        canConfirm() {
            return this.selectedStatus;
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.resetForm();
            }
        },
    },
    methods: {
        normalizeStatus(status) {
            if (!status) return '';
            return String(status).toLowerCase();
        },
        resetForm() {
            this.selectedStatus = '';
            this.comment = '';
            this.loading = false;
        },
        closeModal() {
            if (this.loading) return;
            this.$emit('close');
        },
        async confirmChange() {
            if (!this.canConfirm || this.loading) return;

            this.loading = true;

            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                const response = await fetch(`/api/campaigns/${this.campaign.id}/change-status`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        status: this.selectedStatus,
                        comment: this.comment.trim(),
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Ошибка при изменении статуса');
                }

                const result = await response.json();

                this.$emit('status-changed', {
                    campaignId: this.campaign.id,
                    newStatus: this.selectedStatus,
                    newComment: this.comment.trim(),
                });

                this.$notify?.({
                    title: 'Успех',
                    message: 'Статус кампании успешно изменен',
                    type: 'success',
                });

                this.closeModal();

            } catch (error) {
                this.$notify?.({
                    title: 'Ошибка',
                    message: error.message || 'Не удалось изменить статус кампании',
                    type: 'error',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
}

.modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    color: #374151;
}

.modal-body {
    padding: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
}

.form-control {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control:disabled {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
}

textarea.form-control {
    resize: vertical;
    min-height: 80px;
}

.char-count {
    text-align: right;
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px;
    border-top: 1px solid #e5e7eb;
}
</style>
